import { supabase } from '../lib/supabaseClient';

export interface Department {
  id: string;
  name: string;
  code: string;
  leader_id: string | null;
  member_count: number;
  is_active: boolean;
  description: string | null;
  created_at: string;
  updated_at: string;
}

class SupabaseDepartmentService {
  private listeners: Array<() => void> = [];

  // Subscribe to department changes
  subscribe(listener: () => void) {
    this.listeners.push(listener);
    
    // Set up real-time subscription
    const channel = supabase
      .channel('departments_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'departments' }, () => {
        this.notifyListeners();
      })
      .subscribe();

    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
      supabase.removeChannel(channel);
    };
  }

  // Notify all listeners of changes
  private notifyListeners() {
    this.listeners.forEach(listener => listener());
  }

  // Get all departments
  async getAllDepartments(): Promise<Department[]> {
    const { data, error } = await supabase
      .from('departments')
      .select('*')
      .order('name');

    if (error) {
      console.error('Error fetching departments:', error);
      return [];
    }

    return data as Department[];
  }

  // Get active departments only
  async getActiveDepartments(): Promise<Department[]> {
    const { data, error } = await supabase
      .from('departments')
      .select('*')
      .eq('is_active', true)
      .order('name');

    if (error) {
      console.error('Error fetching active departments:', error);
      return [];
    }

    return data as Department[];
  }

  // Get department by ID
  async getDepartmentById(departmentId: string): Promise<Department | null> {
    const { data, error } = await supabase
      .from('departments')
      .select('*')
      .eq('id', departmentId)
      .single();

    if (error) {
      console.error('Error fetching department:', error);
      return null;
    }

    return data as Department;
  }

  // Get department by leader
  async getDepartmentByLeader(leaderId: string): Promise<Department | null> {
    const { data, error } = await supabase
      .from('departments')
      .select('*')
      .eq('leader_id', leaderId)
      .single();

    if (error) {
      console.error('Error fetching department by leader:', error);
      return null;
    }

    return data as Department;
  }

  // Create department
  async createDepartment(departmentData: {
    name: string;
    code: string;
    leader_id?: string | null;
    description?: string | null;
  }): Promise<Department | null> {
    const { data, error } = await supabase
      .from('departments')
      .insert([{
        ...departmentData,
        member_count: 0,
        is_active: true,
      }])
      .select()
      .single();

    if (error) {
      console.error('Error creating department:', error);
      return null;
    }

    this.notifyListeners();
    return data as Department;
  }

  // Update department
  async updateDepartment(departmentId: string, updates: Partial<Department>): Promise<boolean> {
    // Validate leader_id exists if being set
    if (updates.leader_id !== undefined && updates.leader_id !== null) {
      const { data: userData } = await supabase
        .from('users')
        .select('id')
        .eq('id', updates.leader_id)
        .single();

      if (!userData) {
        console.error(`Cannot set leader_id: User ${updates.leader_id} does not exist`);
        return false;
      }
    }

    const { error } = await supabase
      .from('departments')
      .update(updates)
      .eq('id', departmentId);

    if (error) {
      console.error('Error updating department:', error);
      return false;
    }

    this.notifyListeners();
    return true;
  }

  // Delete department (set inactive)
  async deleteDepartment(departmentId: string): Promise<boolean> {
    const { error } = await supabase
      .from('departments')
      .update({ is_active: false })
      .eq('id', departmentId);

    if (error) {
      console.error('Error deleting department:', error);
      return false;
    }

    this.notifyListeners();
    return true;
  }

  // Update member count for a department
  async updateMemberCount(departmentId: string): Promise<void> {
    // Count users in this department
    const { count, error } = await supabase
      .from('users')
      .select('*', { count: 'exact', head: true })
      .eq('department_id', departmentId);

    if (error) {
      console.error('Error counting department members:', error);
      return;
    }

    // Update department with new count
    await supabase
      .from('departments')
      .update({ member_count: count || 0 })
      .eq('id', departmentId);

    this.notifyListeners();
  }
}

// Create and export singleton instance
export const supabaseDepartmentService = new SupabaseDepartmentService();
